"use client";

import React from "react";
import { Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
export default function InquiryPortal() {
  return <section data-section-id="24225" id="start-your-quote" className="bg-background text-foreground relative overflow-hidden py-24 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[128px] opacity-60 mix-blend-multiply animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-secondary/30 rounded-full blur-[128px] opacity-40 mix-blend-multiply" />
      </div>

      <div className="relative w-full max-w-2xl mx-auto z-10">
        <div className="text-center mb-10 space-y-2">
          <h3 className="text-primary font-mono text-sm tracking-widest uppercase font-bold">
            Get Your Free Solar Quote
          </h3>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Illuminate Your Energy Savings
          </h2>
          <p className="text-muted-foreground text-lg max-w-lg mx-auto">Ready to maximise your solar efficiency? Fill out the form below for a professional cleaning quote.</p>
        </div>

        <Card className="border-border/50 bg-white/60 backdrop-blur-2xl shadow-xl shadow-primary/5 overflow-hidden rounded-[2rem]">
          <CardHeader className="border-b border-border/50 pb-6 bg-accent/30">
            <CardTitle className="text-2xl font-bold text-foreground">Quote Request</CardTitle>
            <CardDescription className="text-muted-foreground">
              Tell us about your solar system. Our experts will get back to you with a custom quote within 24 hours.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formKey="start_quote_form_home" formId="start_quote_form_home" sectionName="start-your-quote" defaultValues={{
            name: "",
            email: "",
            organization: "",
            phone: "",
            subject: "Residential Cleaning",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            organization: {
              required: false
            },
            phone: {
              required: false,
              pattern: "phone"
            },
            subject: {
              required: true
            },
            message: {
              required: true,
              minLength: 20,
              maxLength: 2000
            }
          }} submitText="Request Quote" successMessage="Thank you for choosing Power Shine. We'll review your system details and contact you within 24 hours.">
              <InquiryFormContent />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-20 w-20 rounded-full bg-primary/20 flex items-center justify-center mb-2">
          <CircleCheck className="h-10 w-10 text-primary" />
        </div>
        <h3 className="text-3xl font-bold text-foreground">Quote Request Sent!</h3>
        <p className="text-muted-foreground text-lg max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/50 hover:bg-primary/10 hover:text-primary rounded-full px-8">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground/90">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Alex Smith" {...field} className="bg-white border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-xl" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="alex@example.com" {...field} className="bg-white border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground/90">Property Type</FormLabel>
              <FormControl>
                <Input placeholder="Residential or Commercial" {...field} className="bg-white border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-foreground/90">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0466 880 255" {...field} className="bg-white border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 rounded-xl" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">Optional – for a faster follow-up</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-semibold text-foreground/90">Service Required</FormLabel>
            <FormControl>
              <div className="relative">
                <select {...field} className="flex h-11 w-full items-center justify-between rounded-xl border border-border bg-white px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-3 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 appearance-none text-foreground transition-all duration-300">
                  <option value="Residential Cleaning" className="bg-white text-foreground">Residential Cleaning</option>
                  <option value="Commercial Cleaning" className="bg-white text-foreground">Commercial Cleaning</option>
                  <option value="Other" className="bg-white text-foreground">Other Inquiry</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                  <svg className="h-5 w-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-semibold text-foreground/90">System Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your roof type or number of panels..." {...field} className="min-h-[150px] bg-white border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-all duration-300 resize-y rounded-xl" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3 animate-in slide-in-from-top-2">
          <CircleAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-bold text-destructive">Submission Failed</h4>
            <p className="text-sm text-destructive/80 mt-1">{submitError}</p>
          </div>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold bg-gradient-to-r from-primary to-[#ffb700] text-primary-foreground hover:opacity-90 transition-all shadow-lg shadow-primary/30 rounded-full">
        {isSubmitting ? <>
            <Loader className="mr-2 h-6 w-6 animate-spin" />
            Processing Quote...
          </> : <>
            Request Professional Quote
            <Send className="ml-2 h-5 w-5" />
          </>}
      </Button>
    </Form>;
}